import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { change, fieldInputPropTypes, fieldMetaPropTypes } from 'redux-form';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import FaCloudUpload from 'react-icons/lib/fa/cloud-upload';

import FileLine from '../../FileLine/FileLine';

import './DropElement.scss';

const uploadLogger = (fileName, progressEvent) => console.log(
  fileName,
  ((progressEvent.loaded / progressEvent.total) * 100).toFixed(0),
  '%',
);

function DropElement({
  input,
  label,
  bemClass,
  meta: { dispatch, form },
  dropZoneTxt,
  uploadEndpoint,
  hidden,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [isError, setIsError] = useState(false);
  const onDrop = useCallback(() => {
    setIsError(false);
  }, []);
  const onDropRejected = useCallback((files) => {
    setIsError(files.map(({ name }) => `${name} is too big.`));
  }, []);
  const onDropAccepted = useCallback(async (files) => {
    const errors = [];
    setIsUploading(true);
    const uploadPromiseArray = files.map(async (file) => {
      const { signedUrl, fileId, tagHeader } = await axios.get(`${process.env.FILES_API}/${uploadEndpoint}/files/url?filename=${encodeURIComponent(file.name)}&isPublic=true&hidden=false&contentType=${file.type}`).then(({ data }) => data);
      const formData = new FormData();
      formData.append('file', file);
      return axios
        .put(signedUrl, file, {
          onUploadProgress: (event) => uploadLogger(file.name, event),
          headers: {
            pragma: 'no-cache',
            'cache-control': 'no-cache',
            'x-amz-tagging': tagHeader,
            'Content-Type': file.type,
          },
        })
        .then(() => axios.put(`${process.env.FILES_API}/weborders/files/${fileId}/tag`, [
          { Key: 'UploadedBy', Value: window.anowUserEmail || 'Anow Guest' },
          { Key: 'UploaderFirstName', Value: window.UserFirstName || 'Anow' },
          { Key: 'UploaderLastName', Value: window.UserLastName || 'Guest' },
        ]))
        .then(() => ({
          contentType: file.type,
          dateModified: new Date().toDateString(),
          id: fileId,
          name: file.name,
        }))
        .catch(() => {
          errors.push(`Can not upload file ${file.name}`);
          return null;
        });
    });
    const uploadedFiles = await Promise.all(uploadPromiseArray);
    const successfulUploadedFiles = uploadedFiles.filter((el) => el);
    setIsUploading(false);
    if (errors?.length > 0) {
      setIsUploading(false);
      setIsError(errors);
    }
    if (Array.isArray(input.value)) {
      const newValues = input.value.concat(successfulUploadedFiles);
      dispatch(change(form, input.name, newValues));
    } else {
      dispatch(change(form, input.name, successfulUploadedFiles));
    }
  }, [input.value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropAccepted,
    onDropRejected,
    maxSize: 104857600,
    disabled: isUploading,
  });
  if (hidden) {
    return null;
  }
  const uploadingAnimation = isUploading ? (
    <div className={`${bemClass}__uploading`}>
      Uploading files
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </div>
  ) : (
    <div className={`${bemClass}__text`}>{dropZoneTxt}</div>
  );
  return (
    <div className="row">
      <div className="col-12">
        <div className={`${bemClass}`}>
          <span className={`${bemClass}__label`}>{label}</span>
          <div
            className={`${bemClass}__dropzone w-100
            ${isDragActive ? `${bemClass}__dropzone-active` : ''}
            ${isUploading ? `${bemClass}__disabled` : ''}
          `}
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            {uploadingAnimation}
          </div>
          {isError?.length > 0 && (
            <div className={`invalid-feedback ${bemClass}__feedback`}>
              {isError.map((el) => <div key={el}>{el}</div>)}
            </div>
          )}
          <div className="row">
            {Array.isArray(input.value)
              && input.value.map((el) => (
                <div key={el.id} className="col-6">
                  <FileLine fileName={el.name} />
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

DropElement.propTypes = {
  input: PropTypes.shape(fieldInputPropTypes).isRequired,
  meta: PropTypes.shape(fieldMetaPropTypes).isRequired,
  label: PropTypes.string,
  bemClass: PropTypes.string,
  dropZoneTxt: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  uploadEndpoint: PropTypes.string.isRequired,
  hidden: PropTypes.bool,
};

DropElement.defaultProps = {
  hidden: false,
  label: null,
  bemClass: 'dropzone',
  dropZoneTxt: (
    <div>
      <FaCloudUpload size={25} className="mr-2 green" />
      Drag & drop files to
      attach, or
      <span className="blue-link ml-1">browse</span>
      .
    </div>
  ),
};

export default DropElement;
